import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronDown, PartyPopper, Users, Candy, GraduationCap } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const HERO_BG = "https://images.unsplash.com/photo-1759330203240-b89ccee8840f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxpbmRvb3IlMjBwbGF5Z3JvdW5kJTIwYmFsbCUyMHBpdCUyMHNsaWRlfGVufDB8fHx8MTc3MzE2ODMwNnww&ixlib=rb-4.1.0&q=85";

export const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section 
      data-testid="hero-section" 
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src={HERO_BG} 
          alt="Indoor playground" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-transparent" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-32 right-20 w-64 h-64 bg-brand-orange/20 rounded-full blur-3xl hidden lg:block" />
      <div className="absolute bottom-32 left-20 w-48 h-48 bg-brand-pink/20 rounded-full blur-3xl hidden lg:block" />
      <div className="absolute top-1/2 right-1/3 w-32 h-32 bg-brand-blue/20 rounded-full blur-2xl hidden lg:block" />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-32 md:py-40">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-brand-orange/10 text-brand-orange font-heading font-bold text-sm mb-6">
              {t('hero.badge')}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6"
          >
            {t('hero.title1')}{' '}
            <span className="gradient-text">{t('hero.titleHighlight')}</span>{' '}
            {t('hero.title2')}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-body text-lg md:text-xl text-gray-800 mb-10 leading-relaxed bg-white/80 backdrop-blur-sm px-6 py-4 rounded-2xl shadow-lg"
          >
            {t('hero.description')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-wrap gap-4"
          >
            <Link to="/book/birthday">
              <Button 
                data-testid="hero-book-birthday-btn"
                className="bg-brand-orange hover:bg-orange-600 text-white rounded-full px-8 py-6 font-heading font-bold text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105"
              >
                <PartyPopper className="w-5 h-5 mr-2" />
                {t('hero.bookBirthday')}
              </Button>
            </Link>
            <a href="#services">
              <Button 
                data-testid="hero-explore-btn"
                variant="outline"
                className="border-2 border-brand-orange text-brand-orange hover:bg-brand-orange/10 rounded-full px-8 py-6 font-heading font-bold text-lg transition-all"
              >
                {t('hero.exploreServices')}
              </Button>
            </a>
          </motion.div>

          {/* Quick Service Cards */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
          >
            <Link to="/book/birthday" className="group">
              <div className="flex items-center gap-3 px-5 py-3 bg-white rounded-2xl shadow-md hover:shadow-lg transition-all group-hover:scale-105">
                <div className="p-2 rounded-xl bg-brand-orange/10">
                  <PartyPopper className="w-5 h-5 text-brand-orange" />
                </div>
                <span className="font-heading font-bold text-gray-700 text-sm">{t('hero.birthdayParties')}</span>
              </div>
            </Link>
            <Link to="/book/mascot" className="group">
              <div className="flex items-center gap-3 px-5 py-3 bg-white rounded-2xl shadow-md hover:shadow-lg transition-all group-hover:scale-105">
                <div className="p-2 rounded-xl bg-brand-blue/10">
                  <Users className="w-5 h-5 text-brand-blue" />
                </div>
                <span className="font-heading font-bold text-gray-700 text-sm">{t('hero.mascots')}</span>
              </div>
            </Link>
            <Link to="/book/candy-bar" className="group">
              <div className="flex items-center gap-3 px-5 py-3 bg-white rounded-2xl shadow-md hover:shadow-lg transition-all group-hover:scale-105">
                <div className="p-2 rounded-xl bg-brand-pink/10">
                  <Candy className="w-5 h-5 text-brand-pink" />
                </div>
                <span className="font-heading font-bold text-gray-700 text-sm">{t('hero.candyBar')}</span>
              </div>
            </Link>
            <Link to="/register/stem" className="group">
              <div className="flex items-center gap-3 px-5 py-3 bg-white rounded-2xl shadow-md hover:shadow-lg transition-all group-hover:scale-105">
                <div className="p-2 rounded-xl bg-green-100">
                  <GraduationCap className="w-5 h-5 text-green-600" />
                </div>
                <span className="font-heading font-bold text-gray-700 text-sm">{t('hero.stemEducation')}</span>
              </div>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <a href="#services" className="flex flex-col items-center gap-2 text-gray-400 hover:text-brand-orange transition-colors">
          <span className="text-sm font-body">{t('hero.scrollToExplore')}</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ChevronDown className="w-6 h-6" />
          </motion.div>
        </a>
      </motion.div>
    </section>
  );
};

export default HeroSection;
